import { useCallback } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'

import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { PolicyRow } from './policy-row'
import type { PolicyDraftListItem } from '@/types/policy'
import type { SortConfig } from '@/hooks/use-policies'

interface PoliciesTableProps {
  /** List of policies to display */
  policies: PolicyDraftListItem[]
  /** Set of selected policy IDs */
  selectedIds: Set<string>
  /** Callback to toggle selection of a policy */
  onToggleSelect: (id: string) => void
  /** Callback to select/deselect all policies */
  onSelectAll: () => void
  /** Whether the table is in loading state */
  isLoading: boolean
  /** Current sort configuration */
  sortConfig: SortConfig
  /** Callback when sort changes */
  onSortChange: (column: SortConfig['column']) => void
  /** Callback when view action is clicked */
  onView: (policy: PolicyDraftListItem) => void
  /** Callback when edit action is clicked */
  onEdit: (policy: PolicyDraftListItem) => void
  /** Callback when delete action is clicked */
  onDelete: (policy: PolicyDraftListItem) => void
}

type SortableColumn = SortConfig['column']

/**
 * Loading skeleton for table rows
 */
function TableSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <Skeleton className="h-4 w-4" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-[180px]" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-[80px]" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-[150px]" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-[100px]" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-[100px]" />
          </TableCell>
          <TableCell>
            <div className="flex gap-1">
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

/**
 * Sort icon component for column headers
 */
function SortIcon({
  column,
  sortConfig,
}: {
  column: SortableColumn
  sortConfig: SortConfig
}) {
  if (sortConfig.column !== column) {
    return <ArrowUpDown className="ml-2 h-4 w-4" />
  }
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  )
}

/**
 * Policies table component with sortable columns and selection
 * Column order: Checkbox | Name | Status | Created By | Created | Updated | Actions
 */
export function PoliciesTable({
  policies,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  isLoading,
  sortConfig,
  onSortChange,
  onView,
  onEdit,
  onDelete,
}: PoliciesTableProps) {
  const allSelected =
    policies.length > 0 && policies.every((p) => selectedIds.has(p.id))
  const someSelected = policies.some((p) => selectedIds.has(p.id))

  const handleSelectAll = useCallback(() => {
    onSelectAll()
  }, [onSelectAll])

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
                aria-label="Select all policies"
                {...(someSelected && !allSelected
                  ? { 'data-state': 'indeterminate' }
                  : {})}
              />
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="-ml-3 h-8 data-[state=open]:bg-accent"
                onClick={() => onSortChange('name')}
              >
                Name
                <SortIcon column="name" sortConfig={sortConfig} />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="-ml-3 h-8 data-[state=open]:bg-accent"
                onClick={() => onSortChange('status')}
              >
                Status
                <SortIcon column="status" sortConfig={sortConfig} />
              </Button>
            </TableHead>
            <TableHead>Created By</TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="-ml-3 h-8 data-[state=open]:bg-accent"
                onClick={() => onSortChange('createdAt')}
              >
                Created
                <SortIcon column="createdAt" sortConfig={sortConfig} />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="-ml-3 h-8 data-[state=open]:bg-accent"
                onClick={() => onSortChange('updatedAt')}
              >
                Updated
                <SortIcon column="updatedAt" sortConfig={sortConfig} />
              </Button>
            </TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableSkeleton />
          ) : policies.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={7}
                className="h-24 text-center text-muted-foreground"
              >
                No policies found.
              </TableCell>
            </TableRow>
          ) : (
            policies.map((policy) => (
              <PolicyRow
                key={policy.id}
                policy={policy}
                isSelected={selectedIds.has(policy.id)}
                onToggleSelect={onToggleSelect}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
